import { Button, Spinner } from "@fluentui/react-components";
import { ArrowUndoFilled, Open24Filled } from "@fluentui/react-icons";
import { DeleteIcon, EditIcon, SaveIcon } from "@fluentui/react-icons-mdl2";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { BenefitCategoryDialog } from "components/BenefitCategoryDialog/BenefitCategoryDialog";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { ROUTES } from "library/constants";
import { IBenefitCategory, IRowItem } from "library/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDeleteBenefitCategory } from "state/queries/useDeleteBenefitCategory";
import { useUpdateBenefitCategory } from "state/queries/useUpdateBenefitCategory";

type Props = {
  item: IRowItem;
  countryCode: number | string;
  clientInternalNumberGOS?: number | string;
  withOpenCategory?: boolean;
  disabled?: boolean;
};

export function CategoryActionsCell({
  item,
  countryCode,
  clientInternalNumberGOS = 0,
  withOpenCategory,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const { palette } = useAppTheme();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const navigate = useNavigate();
  const {
    mutate: updateCategory,
    data: updatedData,
    isLoading: updateInProgress,
  } = useUpdateBenefitCategory(false);

  const goToCategoryImplementations = () => {
    navigate({
      pathname: `${ROUTES.COMPANY_PROFILE}/${item.benefitCategoryTag}/${item?.countryCode}/${item.clientInternalNumberGos}`,
      // search: createSearchParams({
      //   countryCodeId: item?.countryCode?.toString() || "",
      //   clientInternalNumberGOS:
      //     item?.clientInternalNumberGos?.toString() || "",
      // }).toString(),
    });
  };
  const { mutate: onDeleteBenefitCategory, isLoading: deleteInProgress } =
    useDeleteBenefitCategory();

  const handleEditOpen = useCallback((state) => {
    setEditOpen(state);
  }, []);

  const toggleDeleteConfirmationState = useCallback(() => {
    setDeleteConfirmationOpen((prev) => !prev);
  }, []);

  const handleDeleteBenefitCategory = useCallback(() => {
    if (!item) return;
    onDeleteBenefitCategory(
      {
        benefitCategoryTag: Number(item.benefitCategoryTag),
        clientInternalNumberGOS: Number(item.clientInternalNumberGos),
        countryCode: Number(item.countryCode),
        rowVersion: item.rowVersion || "",
      },
      {
        onSuccess: () => {
          toggleDeleteConfirmationState();
          toast.success(t("hbh.benefitCategoryDeletedSuccessfully.label"));
        },
      }
    );
  }, [t, toggleDeleteConfirmationState]);

  const loading = deleteInProgress || updateInProgress;

  return (
    <>
      <div>
        {!item?.isEdited ? (
          <Flex width={"100%"} justify="center">
            <Button
              style={{
                color: palette.purpleDark,
              }}
              size="small"
              disabled={loading || disabled}
              appearance="transparent"
              icon={<EditIcon />}
              onClick={() => {
                setEditOpen(true);
              }}
            />
            {withOpenCategory && (
              <Button
                style={{
                  color: palette.purpleDark,
                }}
                icon={<Open24Filled />}
                disabled={loading}
                appearance="transparent"
                size="small"
                onClick={goToCategoryImplementations}
              />
            )}
            <Button
              style={{
                color: palette.redDark,
              }}
              appearance={"transparent"}
              disabled={loading || disabled}
              icon={<DeleteIcon />}
              onClick={() => {
                setDeleteConfirmationOpen(true);
              }}
            />
          </Flex>
        ) : loading ? (
          <>
            <Spinner size="extra-small" />
            <AppSpinner />
          </>
        ) : (
          <Flex width={"100%"} justify="center" gap={"2px"}>
            {withOpenCategory && (
              <Button
                style={{
                  color: palette.purpleDark,
                }}
                icon={<Open24Filled />}
                disabled={loading}
                appearance="transparent"
                size="small"
                onClick={goToCategoryImplementations}
              />
            )}
            <Button
              style={{
                color: palette.purpleDark,
              }}
              icon={<SaveIcon />}
              disabled={loading || disabled}
              appearance="transparent"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (item.isEdited) {
                  updateCategory(
                    {
                      countryCode: Number(item.countryCode),
                      clientInternalNumberGOS: Number(
                        item.clientInternalNumberGos
                      ),
                      benefitCategoryTag: Number(item.benefitCategoryTag),

                      payload: {
                        ...item,

                        ...((item.coverPhoto?.base64String ||
                          item.coverPhoto?.path) && {
                          backgroundPictureContent:
                            item.coverPhoto?.base64String,
                          backgroundPictureName: item.coverPhoto?.name,
                          backgroundPicturePath: item.coverPhoto?.path,
                          backgroundPictureMimeType: item.coverPhoto?.type,
                        }),
                        ...(!item.coverPhoto?.name && {
                          backgroundPictureContent: null,
                          backgroundPictureName: null,
                          backgroundPicturePath: null,
                          backgroundPictureMimeType: null,
                        }),
                      } as IBenefitCategory,
                    },
                    {
                      onSuccess: (data) => {
                        const updatedItem = {
                          ...data,
                          coverPhoto: {
                            name: data.backgroundPicturePath || "",
                            base64String: data.backgroundPictureContent || "",
                            type: data.backgroundPictureMimeType || "",
                          },
                          defaultState: {
                            ...data,
                            coverPhoto: {
                              name: data.backgroundPicturePath || "",
                              base64String: data.backgroundPictureContent || "",
                              type: data.backgroundPictureMimeType || "",
                            },
                            id: data.benefitCategoryTag,
                          },
                          rowVersion: data.rowVersion,
                          isEdited: false,
                          id: data.benefitCategoryTag,
                        };
                        item.updateState(updatedItem);
                        toast.success(
                          t("hbh.benefitCategoryUpdatedSuccessfully.label")
                        );
                      },
                    }
                  );
                }
              }}
            />
            <Button
              style={{
                color: palette.redDark,
              }}
              icon={<ArrowUndoFilled />}
              disabled={loading || disabled}
              appearance="transparent"
              size="small"
              onClick={() => {
                const updatedRowItem = updatedData as IRowItem;
                if (updatedRowItem) {
                  const updatedItem = {
                    ...updatedRowItem,
                    coverPhoto: {
                      name: updatedRowItem.backgroundPicturePath || null,
                      base64String:
                        updatedRowItem.backgroundPictureContent || null,
                      type: updatedRowItem.backgroundPictureMimeType || null,
                    },
                    defaultState: {
                      ...updatedRowItem,
                      coverPhoto: {
                        name: updatedRowItem.backgroundPicturePath || null,
                        1: updatedRowItem.backgroundPictureContent || null,
                        type: updatedRowItem.backgroundPictureMimeType || null,
                      },
                      id: updatedRowItem.benefitCategoryTag,
                    },
                    id: updatedRowItem.benefitCategoryTag,

                    isEdited: false,
                  };

                  item.updateState(updatedItem, true);
                  return;
                }

                const updatedItem = {
                  ...item.defaultState,
                  defaultState: item.defaultState,
                  isEdited: false,
                } as any;
                item.updateState(updatedItem, true);
              }}
            />
            <Button
              style={{
                color: palette.redDark,
              }}
              size="small"
              appearance={"transparent"}
              disabled={loading || disabled}
              icon={<DeleteIcon />}
              onClick={() => {
                setDeleteConfirmationOpen(true);
              }}
            />
          </Flex>
        )}
      </div>
      {editOpen && (
        <BenefitCategoryDialog
          clientInternalNumberGOS={Number(clientInternalNumberGOS)}
          countryCode={Number(countryCode)}
          open={editOpen}
          setOpen={handleEditOpen}
          benefitCategory={item}
        />
      )}
      {deleteConfirmationOpen && (
        <ConfirmationDialog
          isOpen={deleteConfirmationOpen}
          toggleOpen={toggleDeleteConfirmationState}
          onConfirm={handleDeleteBenefitCategory}
          description={t("hbh.deleteObject.label", {
            object: item?.name,
          })}
          confirmLabel={t("greco.delete")}
          title={`${t("hbh.deleteCategory.label")}: ${item?.name} `}
        />
      )}
    </>
  );
}
